import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createOrganizationUnitList,
  deleteOrganizationUnitList,
  fetchOrganizationUnitListById,
  fetchOrganizationUnitLists,
  updateOrganizationUnitList,
} from './OrganizationUnitLists.services.ts';
import { toast } from 'react-toastify';
import i18n from '../../utils/i18n.ts';
import type { ApiError } from '../../types/App.ts';
import { useMsal } from '@azure/msal-react';
import { orderBy } from 'lodash';

export const useService = ({
  organizationUnitListId,
}: {
  organizationUnitListId: number | null;
}) => {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();

  const hasAccount = accounts.length > 0;

  const createMutation = useMutation({
    mutationFn: createOrganizationUnitList,
    onSuccess: () => {
      toast.success(
        i18n.t('success.api.post-item', { item: i18n.t('module.organization-unit-list') }),
      );
      queryClient.invalidateQueries({ queryKey: ['organizationUnitLists'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateOrganizationUnitList,
    onSuccess: () => {
      toast.success(
        i18n.t('success.api.put-item', { item: i18n.t('module.organization-unit-list') }),
      );
      queryClient.invalidateQueries({ queryKey: ['organizationUnitLists'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOrganizationUnitList,
    onSuccess: () => {
      toast.success(
        i18n.t('success.api.delete-item', { item: i18n.t('module.organization-unit-list') }),
      );
      queryClient.invalidateQueries({ queryKey: ['organizationUnitLists'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });
  const { data: unit } = useQuery({
    queryKey: ['organizationUnitListById', organizationUnitListId],
    queryFn: () => fetchOrganizationUnitListById(organizationUnitListId),
    enabled: !!organizationUnitListId && hasAccount,
    retry: false,
  });
  const { data: units, isLoading } = useQuery({
    queryKey: ['organizationUnitLists'],
    queryFn: fetchOrganizationUnitLists,
    enabled: hasAccount,
    retry: false,
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    unit,
    units:
      units?.length > 0
        ? orderBy(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            units.map((unit) => ({ ...unit, name: unit.title.replace(/^\d+\s*/, '') })),
            'order',
            'asc',
          )
        : [],
    isLoading,
  };
};